import {
  pathApiExchangeGiftRewards,
  pathApiGetExchangeGiftRewards,
  pathApiGiftExchangeTransactionDetails,
  pathApiGiftExchangeTransactionHistory,
  pathApiGiftInventory,
  pathApiGiftsList,
  pathApiGiftsReceivedCall,
  pathApiGiftsReceivedGroup,
  pathApiGiftsReceivedPost,
  pathApiGiftsReceivedUser,
  pathApiReceivedGiftTransactionDetails,
  pathApiSendGiftCall,
  pathApiSendGiftGroup,
  pathApiSendGiftPost,
  pathApiSendGiftUser,
} from 'lib/paths/api'
import {
  GiftExchangeTransactionDetailsQueryResponse,
  GiftExchangeTransactionHistoryQueryResponse,
  ReceivedGiftTransactionDetailsQueryResponse,
} from 'lib/_types'
import { Gift, UnifiedReceivedGift } from 'lib/_types/gift'
import { yayApi } from '../baseApi'

export type GiftItem = {
  id: number
  quantity: number
  slug: string
  icon: string
}

export type ReceivedGift = {
  sent_at: number
  gift_item: GiftItem
  sender: {
    id: number
    username: string
    nickname: string
    profile_icon: string
    profile_icon_thumbnail: string
  }
  anonymous: boolean
}

export type ReceivedGiftWithRecipient = ReceivedGift & {
  receiver: {
    id: number
    username: string
    nickname: string
    profile_icon: string
    profile_icon_thumbnail: string
  }
}

export type GiftReward = {
  id: number
  rewarded_amount: number
  requirements: Array<{
    requires_amount: number
    received_count: number
    gift: Gift
  }>
}

export const giftsApi = yayApi
  .enhanceEndpoints({
    addTagTypes: [
      'Gifts',
      'GiftInventory',
      'UserReceivedGifts',
      'PostReceivedGifts',
      'GroupReceivedGifts',
      'CallReceivedGifts',
      'GiftRewardCombinations',
      'ReceivedGiftTransactionDetails',
      'GiftExchangedTransactionHistory',
      'GiftExchangedTransactionDetails',
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getGiftInventory: build.query<
        {
          result: 'success'
          received_gifts: Array<UnifiedReceivedGift>
          total_count: number
        },
        void
      >({
        providesTags: ['GiftInventory'],
        query: () => ({
          url: `${pathApiGiftInventory}`,
        }),
      }),

      getGiftsList: build.query<
        {
          result: 'success'
          gifts: Array<Gift>
          total_count: number
        },
        {
          currency?: string
        }
      >({
        providesTags: ['Gifts'],
        query: ({ currency }) => ({
          url: `${pathApiGiftsList}`,
          params: {
            currency: currency ?? undefined,
            number: 20,
          },
        }),
      }),

      getUserReceivedGifts: build.query<
        {
          sent_gifts: Array<ReceivedGift>
          next_page_value: string
        },
        {
          userId: number
          from?: number
        }
      >({
        providesTags: ['UserReceivedGifts'],
        query: ({ userId, from = undefined }) => ({
          url: pathApiGiftsReceivedUser({ userId: userId }),
          params: {
            number: 10,
            from: from ?? undefined,
          },
        }),
      }),

      getPostReceivedGifts: build.query<
        {
          sent_gifts: Array<ReceivedGift>
          next_page_value: string
        },
        {
          postId: number
          from?: number
        }
      >({
        providesTags: ['PostReceivedGifts'],
        query: ({ postId, from = undefined }) => ({
          url: pathApiGiftsReceivedPost({ postId: postId }),
          params: {
            number: 10,
            from: from ?? undefined,
          },
        }),
      }),

      getGroupReceivedGifts: build.query<
        {
          sent_gifts: Array<ReceivedGiftWithRecipient>
          next_page_value: string
        },
        {
          groupId: number
          from?: number
        }
      >({
        providesTags: ['GroupReceivedGifts'],
        query: ({ groupId, from = undefined }) => ({
          url: pathApiGiftsReceivedGroup({ groupId: groupId }),
          params: {
            number: 10,
            from: from ?? undefined,
          },
        }),
      }),

      getCallReceivedGifts: build.query<
        {
          sent_gifts: Array<ReceivedGiftWithRecipient>
          next_page_value: string
        },
        {
          callId: number
          from?: number
        }
      >({
        providesTags: ['CallReceivedGifts'],
        query: ({ callId, from = undefined }) => ({
          url: pathApiGiftsReceivedCall({ callId: callId }),
          params: {
            number: 50,
            from: from ?? undefined,
          },
        }),
      }),

      sendGiftUser: build.mutation<
        any,
        {
          userId: number
          gifts: { [gift: string]: number }
          anonymous?: boolean
        }
      >({
        invalidatesTags: ['UserReceivedGifts'],
        query: ({ userId, gifts, anonymous }) => {
          return {
            url: pathApiSendGiftUser({ userId: userId }),
            method: 'POST',
            dataType: 'json',
            data: {
              anonymous: anonymous ?? false,
              gifts: gifts,
            },
          }
        },
      }),

      sendGiftPost: build.mutation<
        any,
        {
          postId: number
          gifts: { [gift: string]: number }
          anonymous?: boolean
        }
      >({
        invalidatesTags: ['PostReceivedGifts'],
        query: ({ postId, gifts, anonymous }) => {
          return {
            url: pathApiSendGiftPost({ postId: postId }),
            method: 'POST',
            dataType: 'json',
            data: {
              anonymous: anonymous ?? false,
              gifts: gifts,
            },
          }
        },
      }),

      sendGiftGroup: build.mutation<
        any,
        {
          groupId: number
          recipientId: number
          gifts: { [gift: string]: number }
          anonymous?: boolean
        }
      >({
        invalidatesTags: ['GroupReceivedGifts'],
        query: ({ groupId, recipientId, gifts, anonymous }) => {
          return {
            url: pathApiSendGiftGroup({ groupId: groupId }),
            method: 'POST',
            dataType: 'json',
            data: {
              anonymous: anonymous ?? false,
              gifts: gifts,
              receiver_id: recipientId,
            },
          }
        },
      }),

      sendGiftCall: build.mutation<
        any,
        {
          callId: number
          recipientId: number
          gifts: { [gift: string]: number }
          anonymous?: boolean
        }
      >({
        invalidatesTags: ['CallReceivedGifts'],
        query: ({ callId, recipientId, gifts, anonymous }) => {
          return {
            url: pathApiSendGiftCall({ callId: callId }),
            method: 'POST',
            dataType: 'json',
            data: {
              anonymous: anonymous ?? false,
              gifts: gifts,
              receiver_id: recipientId,
            },
          }
        },
      }),

      getGiftRewardCombinations: build.query<
        {
          gift_rewards: Array<GiftReward>
        },
        void
      >({
        providesTags: ['GiftRewardCombinations'],
        query: () => ({
          url: pathApiGetExchangeGiftRewards,
        }),
      }),

      exchangeGiftRewardCombination: build.mutation<
        any,
        {
          id: number
          quantity: number
        }
      >({
        invalidatesTags: ['GiftRewardCombinations'],
        query: ({ id, quantity }) => ({
          url: pathApiExchangeGiftRewards({ giftComboId: id }),
          method: 'POST',
          data: {
            quantity: quantity,
          },
        }),
      }),

      getReceivedGiftTransactionDetails: build.query<
        ReceivedGiftTransactionDetailsQueryResponse,
        { uuid: string }
      >({
        providesTags: ['ReceivedGiftTransactionDetails'],
        query: ({ uuid }) => ({
          url: pathApiReceivedGiftTransactionDetails({
            giftTransactionUuid: uuid,
          }),
          params: {
            gift_transaction_uuid: uuid,
          },
        }),
      }),

      getGiftExchangeTransactionHistory: build.query<
        GiftExchangeTransactionHistoryQueryResponse,
        { from?: string; number: number }
      >({
        providesTags: ['GiftExchangedTransactionHistory'],
        query: ({ from, number }) => ({
          url: pathApiGiftExchangeTransactionHistory,
          params: {
            from: from,
            number: number ?? 10,
          },
        }),
      }),

      getGiftExchangeTransactionDetails: build.query<
        GiftExchangeTransactionDetailsQueryResponse,
        { uuid: string }
      >({
        providesTags: ['GiftExchangedTransactionDetails'],
        query: ({ uuid }) => ({
          url: pathApiGiftExchangeTransactionDetails({
            giftTransactionUuid: uuid,
          }),
          params: {
            gift_transaction_uuid: uuid,
          },
        }),
      }),
    }),
    overrideExisting: false,
  })

export const {
  endpoints,
  useGetGiftInventoryQuery,
  useGetGiftsListQuery,
  useGetUserReceivedGiftsQuery,
  useGetPostReceivedGiftsQuery,
  useGetGroupReceivedGiftsQuery,
  useGetCallReceivedGiftsQuery,
  useSendGiftUserMutation,
  useSendGiftPostMutation,
  useSendGiftGroupMutation,
  useSendGiftCallMutation,
  useGetGiftRewardCombinationsQuery,
  useExchangeGiftRewardCombinationMutation,
  useGetReceivedGiftTransactionDetailsQuery,
  useGetGiftExchangeTransactionHistoryQuery,
  useGetGiftExchangeTransactionDetailsQuery,
} = giftsApi
